import { isURL } from 'validator';

export const updateIsFormDirtyState = (
  isDirtyMap: Map<string, boolean>,
  key: string,
  value: boolean,
  setIsDirtyMap: (map: Map<string, boolean>) => void,
  setIsFormDirty: (isFormDirty: boolean) => void,
) => {
  // Create a shallow copy of the original map
  const newisDirtyMap = new Map(isDirtyMap);
  // Add or update the key-value pair in the copy
  newisDirtyMap.set(key, value);
  // Update the state with the new map
  setIsDirtyMap(newisDirtyMap);

  // Initialize the result to false
  let result = false;
  // Iterate over all values in the map and perform OR operation
  for (const val of newisDirtyMap.values()) {
    result = result || val;
  }

  setIsFormDirty(result);
};

// Sanitizes a string: trims, remove multiple spaces, limits to 255 characters (XSS)
// Returns an empty string if undefined, null, or contains only blank spaces
export const sanitizeString = (input: string | undefined): string => {
  if (input === undefined || input === null || input.trim() === '') {
    return '';
  }
  return input.trim().replace(/\s+/g, ' ').substring(0, 255);
};

export const validateField = (
  value: string | undefined,
  fieldName: string,
  errMsg: string,
): void => {
  if (sanitizeString(value) === '') {
    throw new Error(`${fieldName} ${errMsg}`);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hasEmptyValues = (obj: any): boolean => {
  if (!obj || typeof obj !== 'object') return false;

  return Object.keys(obj).some((key) => {
    const value = obj[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return hasEmptyValues(value);
    }
    return value === '';
  });
};

export const isValidWebsite = (url: string) => {
  if (!url) return false;
  // Add http:// or https:// prefix if missing and then validate
  const formattedUrl = url.startsWith('http://') || url.startsWith('https://') ?
    url :
    `http://${url}`;

  return isURL(formattedUrl, { protocols: ['http', 'https'], require_protocol: true });
};

export const isValidPhoneNumber = (phnNumber: string) => /^(?:\+1\s?|\+1)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/.test(phnNumber);

// URL validation regex that requires a valid domain format
export const isValidWebsiteUrl = (url: string): boolean => {
  const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
  return urlPattern.test(url);
};

export const getFullAddressUS = (
  street?: string,
  city?: string,
  state?: string,
  zipCode?: string,
) => {
  const addressParts = [];

  // Add the street if available
  if (street) addressParts.push(street);

  // Add city if available
  if (city) addressParts.push(city);

  // If both state and zipCode are available, combine them as "state zipCode"
  if (state && zipCode) {
    addressParts.push(`${state} ${zipCode}`);
  } else {
    if (state) addressParts.push(state);
    if (zipCode) addressParts.push(zipCode);
  }

  // Return an empty string if no address parts are available
  return addressParts.length > 0 ? addressParts.join(', ') : '';
};
